import { useTheme } from "@emotion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { mixHighlightColour } from "../../design/colours";
import { pageColourOptions } from "../bits/settings/defaults";
import { getRangeFromSnippet } from "../utils";

export const useSearchHighlight = (
  textContainerRef: React.RefObject<HTMLElement | null>,
  containerRef: React.RefObject<HTMLElement | null>,
  colour: string,
) => {
  const theme = useTheme();
  const isDark = colour === pageColourOptions.black.background;
  const [commonAncestor, setCommonAncestor] = useState<HTMLElement | null>(
    null,
  );
  const highlightRef = useRef<HTMLDivElement>(null);
  const snippetRef = useRef<{
    cleanedSnippet: string;
    sectionId: string;
  }>(null);

  const highlightColours = useMemo(
    () => ({
      selected: mixHighlightColour(
        colour || theme.colours.accent.light._15,
        theme.colours.highlight.blue,
      ),
      default: mixHighlightColour(
        colour || theme.colours.accent.light._15,
        theme.colours.highlight.yellow,
      ),
      highlighted: isDark
        ? theme.colours.highlight.darkYellow
        : mixHighlightColour(
            colour || theme.colours.accent.light._15,
            theme.colours.highlight.yellow,
            90,
          ),
    }),
    [
      colour,
      theme.colours.highlight.blue,
      theme.colours.highlight.yellow,
      theme.colours.highlight.darkYellow,
      theme.colours.accent.light._15,
      isDark,
    ],
  );

  const applyHighlight = useCallback(
    (cleanedSnippet: string, sectionId: string) => {
      const el = document.getElementById(sectionId);
      if (!el) return;
      let range;
      range = getRangeFromSnippet(el, cleanedSnippet);
      // Fallback if the range couldn't be created from the snippet
      if (!range) {
        range = document.createRange();
        range.selectNode(el);
      }

      const container = textContainerRef.current;
      if (!container) return;
      const existing = highlightRef.current;
      if (existing) {
        existing.remove();
        highlightRef.current = null;
      }

      const rects = range.getClientRects();
      if (!rects.length) return;

      const highlightDiv = document.createElement("div");
      highlightDiv.className = "highlight-overlay";
      highlightRef.current = highlightDiv;

      const containerRect = container.getBoundingClientRect();
      const scrollTop = container.scrollTop;
      const scrollLeft = container.scrollLeft;

      if (range.commonAncestorContainer instanceof HTMLElement) {
        setCommonAncestor(range.commonAncestorContainer);
      } else if (range.commonAncestorContainer.parentElement) {
        setCommonAncestor(range.commonAncestorContainer.parentElement);
      }

      Array.from(rects).forEach((rect, i) => {
        const span = document.createElement("span");
        const isAdditionalPadding = rect.height > 200;
        if (!isAdditionalPadding) {
          if (i === 0) {
            span.id = `highlight-selected`;
          }
          span.className = "highlighted";
          span.style.position = "absolute";
          span.style.left = `${rect.left - containerRect.left + scrollLeft}px`;
          span.style.top = `${rect.top - containerRect.top + scrollTop}px`;
          span.style.width = `${rect.width}px`;
          span.style.height = `${rect.height}px`;
          span.style.backgroundColor = highlightColours.default;
          span.style.opacity = "1";
          span.style.pointerEvents = "none";
          span.style.zIndex = "-2";
          highlightDiv.appendChild(span);
        }
      });

      snippetRef.current = { cleanedSnippet, sectionId };

      container.appendChild(highlightDiv);
    },
    [textContainerRef, highlightColours],
  );

  const reapplyHighlight = useCallback(() => {
    if (snippetRef.current) {
      applyHighlight(
        snippetRef.current.cleanedSnippet,
        snippetRef.current.sectionId,
      );
    }
  }, [applyHighlight]);

  const clearHighlight = useCallback(() => {
    const existing = highlightRef.current;
    if (existing) {
      existing.remove();
      highlightRef.current = null;
    }
    snippetRef.current = null;
  }, []);

  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const observedSectionsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const cleanupObservers = () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;

      observedSectionsRef.current.clear();
    };
    let raf: number;
    if (commonAncestor) {
      const setupObservers = () => {
        resizeObserverRef.current?.unobserve(commonAncestor);
        if (!resizeObserverRef.current) {
          resizeObserverRef.current = new ResizeObserver(() => {
            reapplyHighlight();
          });
        }

        if (commonAncestor) {
          resizeObserverRef.current?.observe(commonAncestor);
        }
      };

      const waitForContainer = () => {
        const container = containerRef.current;
        if (!container) {
          raf = requestAnimationFrame(waitForContainer);
          return;
        }
        setupObservers();
      };

      waitForContainer();
    }
    return () => {
      if (raf) cancelAnimationFrame(raf);
      cleanupObservers();
    };
  }, [applyHighlight, commonAncestor, containerRef, reapplyHighlight]);

  return {
    applyHighlight,
    clearHighlight,
    reapplyHighlight,
  };
};
